/**
 * O1 (AB#2199) — the owner booking lifecycle as the portal shows it.
 *
 * ONE place for the owner-facing status words, the action labels and the
 * confirm-step rule, shared by the Calendar booking detail and the Appointments
 * list so both show the same booking truth.
 *
 * Founder terminology (2026-11-04) — never invent other status names:
 *   Pending ("Awaiting confirmation") · Confirmed · Declined by salon ·
 *   Cancelled by customer / salon · Arrived · Started · Completed · No-show.
 *
 * WHICH actions an owner may take is NOT decided here: the backend stamps
 * `allowedTransitions` on every Calendar card and Appointments row from the
 * same lifecycle rules its state machine validates with
 * (Devanza.Core.Helpers.AppointmentLifecycleRules), and the UI renders exactly
 * that list — so it never offers an action the API would reject.
 */
import { AppointmentStatus } from '../types/enums';
import { parseAppointmentStatus } from './enumParsers';

/** Backend wire names of AppointmentStatus (status 2 keeps its wire name `Approved`). */
export type LifecycleStatus =
    | 'Pending' | 'Approved' | 'Arrived' | 'Started' | 'Completed' | 'Cancelled' | 'NoShow';

/** Backend `AppointmentCancellationSource` names. */
export type CancellationSource = 'Customer' | 'Salon' | 'SalonDeclined' | 'Expired' | 'Admin';

const STATUS_BY_NUMBER: Record<number, LifecycleStatus> = {
    [AppointmentStatus.Pending]: 'Pending',
    [AppointmentStatus.Confirmed]: 'Approved',
    [AppointmentStatus.Completed]: 'Completed',
    [AppointmentStatus.Cancelled]: 'Cancelled',
    [AppointmentStatus.NoShow]: 'NoShow',
    6: 'Arrived',
    7: 'Started',
};

/** Normalise a wire status (string name or legacy int) to its backend name. */
export function toLifecycleStatus(raw: unknown): LifecycleStatus | null {
    const n = parseAppointmentStatus(raw);
    return n == null ? null : STATUS_BY_NUMBER[n] ?? null;
}

/** Numeric value the owner status endpoint expects for a target name. */
export function lifecycleStatusValue(status: LifecycleStatus): AppointmentStatus {
    return parseAppointmentStatus(status) as AppointmentStatus;
}

/** i18n key of the owner-facing status label. */
export function bookingStatusLabelKey(raw: unknown, cancellationSource?: string | null): string {
    switch (toLifecycleStatus(raw)) {
        case 'Pending': return 'booking.status.pending';
        case 'Approved': return 'booking.status.confirmed';
        case 'Arrived': return 'booking.status.arrived';
        case 'Started': return 'booking.status.started';
        case 'Completed': return 'booking.status.completed';
        case 'NoShow': return 'booking.status.noShow';
        case 'Cancelled':
            switch (cancellationSource) {
                case 'SalonDeclined': return 'booking.status.declinedBySalon';
                case 'Customer': return 'booking.status.cancelledByCustomer';
                case 'Salon': return 'booking.status.cancelledBySalon';
                default: return 'booking.status.cancelled';
            }
        default: return 'booking.status.unknown';
    }
}

/** i18n key of the extra reason line under a Cancelled status, or null. */
export function bookingStatusReasonKey(raw: unknown, cancellationSource?: string | null): string | null {
    if (toLifecycleStatus(raw) !== 'Cancelled') return null;
    if (cancellationSource === 'Expired') return 'booking.reason.expired';
    if (cancellationSource === 'Admin') return 'booking.reason.admin';
    return null;
}

/**
 * Colour family of a status, following the founder status-colour rule
 * (pending = amber, confirmed = info, in-visit = primary, completed = green,
 * cancelled / no-show = red).
 */
export type StatusTone = 'pending' | 'confirmed' | 'inVisit' | 'completed' | 'ended' | 'neutral';

export function bookingStatusTone(raw: unknown): StatusTone {
    switch (toLifecycleStatus(raw)) {
        case 'Pending': return 'pending';
        case 'Approved': return 'confirmed';
        case 'Arrived':
        case 'Started': return 'inVisit';
        case 'Completed': return 'completed';
        case 'Cancelled':
        case 'NoShow': return 'ended';
        default: return 'neutral';
    }
}

export type LifecycleAction = 'confirm' | 'decline' | 'cancel' | 'step';

export interface OwnerActionView {
    target: LifecycleStatus;
    action: LifecycleAction;
    labelKey: string;
    /** Success toast key. */
    toastKey: string;
    /** Decline / Cancel notify the customer, so they ask for confirmation first. */
    needsConfirm: boolean;
    /** i18n key of the confirm-step question (only when needsConfirm). */
    promptKey?: string;
}

/**
 * Presentation of one backend-allowed target for a booking currently in
 * `from`. A Cancelled target is a "Decline" while the booking is still
 * awaiting confirmation and a "Cancel booking" afterwards. Copy reuses the
 * signed-off AB#2164 calendar keys.
 */
export function ownerActionFor(from: LifecycleStatus | null, target: LifecycleStatus): OwnerActionView {
    switch (target) {
        case 'Approved':
            return { target, action: 'confirm', labelKey: 'calendar.peek.action.confirm', toastKey: 'calendar.peek.toast.confirmed', needsConfirm: false };
        case 'Cancelled':
            return from === 'Pending'
                ? { target, action: 'decline', labelKey: 'calendar.peek.action.decline', toastKey: 'calendar.peek.toast.declined', needsConfirm: true, promptKey: 'calendar.peek.declinePrompt' }
                : { target, action: 'cancel', labelKey: 'calendar.peek.action.cancel', toastKey: 'calendar.peek.toast.cancelled', needsConfirm: true, promptKey: 'calendar.peek.cancelPrompt' };
        case 'Arrived':
            return { target, action: 'step', labelKey: 'calendar.peek.status.arrived', toastKey: 'calendar.peek.statusUpdated', needsConfirm: false };
        case 'Started':
            return { target, action: 'step', labelKey: 'calendar.peek.status.started', toastKey: 'calendar.peek.statusUpdated', needsConfirm: false };
        case 'Completed':
            return { target, action: 'step', labelKey: 'calendar.peek.status.completed', toastKey: 'calendar.peek.statusUpdated', needsConfirm: false };
        case 'NoShow':
            return { target, action: 'step', labelKey: 'calendar.peek.status.noshow', toastKey: 'calendar.peek.statusUpdated', needsConfirm: false };
        default:
            return { target, action: 'step', labelKey: 'calendar.peek.statusUpdated', toastKey: 'calendar.peek.statusUpdated', needsConfirm: false };
    }
}

/** Backend-allowed targets as typed names (unknown names are dropped, never guessed). */
export function allowedTargets(allowedTransitions: readonly string[] | null | undefined): LifecycleStatus[] {
    return (allowedTransitions ?? [])
        .map(name => toLifecycleStatus(name))
        .filter((s): s is LifecycleStatus => s != null);
}
